import { fromJS } from 'immutable';
import { createSelector } from 'reselect';
import GetSystemOverview from '../../api/SystemOverview';
import GetGridSiteStatus from '../../api/GridSiteStatus';
import GetMeterInfo from '../../api/MeterInfo';
import { unwrapGraphQlList } from '../../utilities/graphql';
import { GetGlobalNotificationMessages } from '../../api/GlobalNotificationMessages';

// ACTIONS TYPES
export const FETCHING_BATTERY_DATA = 'customer-portal/system/FETCHING_BATTERY_DATA';
export const FETCHING_METER_INFO = 'customer-portal/system/FETCHING_METER_INFO';
export const FETCHING_SYSTEM_OVERVIEW = 'customer-portal/system/FETCHING_SYSTEM_OVERVIEW';
export const FETCH_BATTERY_DATA_FAILURE = 'customer-portal/system/FETCH_BATTERY_DATA_FAILURE';
export const FETCH_BATTERY_DATA_SUCCESS = 'customer-portal/system/FETCH_BATTERY_DATA_SUCCESS';
export const FETCH_METER_INFO_FAILURE = 'customer-portal/system/FETCH_METER_INFO_FAILURE';
export const FETCH_METER_INFO_SUCCESS = 'customer-portal/system/FETCH_METER_INFO_SUCCESS';
export const FETCH_NOTIFICATIONS_FAILURE = 'customer-portal/app/FETCH_NOTIFICATIONS_FAILURE';
export const FETCH_NOTIFICATIONS_SUCCESS = 'customer-portal/app/FETCH_NOTIFICATIONS_SUCCESS';
export const FETCH_SYSTEM_OVERVIEW_FAILURE = 'customer-portal/system/FETCH_SYSTEM_OVERVIEW_FAILURE';
export const FETCH_SYSTEM_OVERVIEW_SUCCESS = 'customer-portal/system/FETCH_SYSTEM_OVERVIEW_SUCCESS';
export const UPDATE_SYSTEM_OVERVIEW = 'customer-portal/system/UPDATE_SYSTEM_OVERVIEW';
export const UPDATING_SYSTEM_OVERVIEW = 'customer-portal/system/UPDATING_SYSTEM_OVERVIEW';

// ACTION CREATORS
export const fetchSystemOverviewSuccess = system => ({
  type: FETCH_SYSTEM_OVERVIEW_SUCCESS,
  payload: system,
});

export const fetchSystemOverviewFailure = err => ({
  type: FETCH_SYSTEM_OVERVIEW_FAILURE,
  payload: err,
});

export const fetchingSystemOverview = () => ({
  type: FETCHING_SYSTEM_OVERVIEW,
});

export const fetchBatteryDataSuccess = battery => ({
  type: FETCH_BATTERY_DATA_SUCCESS,
  payload: battery,
});

export const fetchBatteryDataFailure = err => ({
  type: FETCH_BATTERY_DATA_FAILURE,
  payload: err,
});

export const updatingSystemOverview = () => ({
  type: UPDATING_SYSTEM_OVERVIEW,
});

export const updateSystemOverviewSuccess = payload => ({
  type: UPDATE_SYSTEM_OVERVIEW,
  payload,
});

export const fetchingMeterInfo = () => ({
  type: FETCHING_METER_INFO,
});

export const fetchMeterInfoSuccess = payload => ({
  type: FETCH_METER_INFO_SUCCESS,
  payload,
});

export const fetchMeterInfoFailure = error => ({
  type: FETCH_METER_INFO_FAILURE,
  payload: error,
});

export const fetchNotificationsFailure = error => ({
  type: FETCH_NOTIFICATIONS_FAILURE,
  payload: error,
});

export const fetchNotificationsSuccess = notifications => ({
  type: FETCH_NOTIFICATIONS_SUCCESS,
  payload: notifications,
});

// THUNKS
export const fetchSystemOverview = () => async dispatch => {
  dispatch(fetchingSystemOverview());
  try {
    const { data } = await GetSystemOverview();
    dispatch(fetchSystemOverviewSuccess(data));
  } catch (error) {
    dispatch(fetchSystemOverviewFailure(error.message));
  }
};

export const shouldFetchSystemOverview = systemState => !systemState.get('loaded') && !systemState.get('loading');

export const fetchSystemOverviewIfNeeded = () => (dispatch, getState) => {
  if (!shouldFetchSystemOverview(getState().system)) {
    return null;
  }

  return dispatch(fetchSystemOverview());
};

export const updateSystemOverview = prospectId => (dispatch, getState) => {
  dispatch(updatingSystemOverview());
  const { system } = getState();
  const allSites = system.get('allSites');
  const newSystemOverview = allSites.find(a => a.prospectId === prospectId);
  return dispatch(updateSystemOverviewSuccess(newSystemOverview));
};

export const fetchMeterInfo = prospectId => async dispatch => {
  dispatch(fetchingMeterInfo());
  try {
    const meterInfo = await GetMeterInfo(prospectId);
    const { data } = meterInfo;
    dispatch(fetchMeterInfoSuccess(data.site.meterInfo));
  } catch (e) {
    dispatch(fetchMeterInfoFailure(e.message));
  }
};

export const fetchMeterInfoIfNeeded = prospectId => (dispatch, getState) => {
  if (getState().system.get('meterInfo')) {
    return null;
  }

  return dispatch(fetchMeterInfo(prospectId));
};

export const fetchBatteryData = (contractId, refreshBatteryData) => async dispatch => {
  try {
    const { data } = await GetGridSiteStatus(contractId, refreshBatteryData);
    dispatch(fetchBatteryDataSuccess(data));
  } catch (error) {
    dispatch(fetchBatteryDataFailure(error.message));
  }
};

export const shouldFetchBatteryData = systemState => !systemState.get('batteryData').get('loaded') && !systemState.get('batteryData').get('loading');

export const fetchBatteryDataIfNeeded = (contractId, refreshBatteryData) => (dispatch, getState) => {
  if (!shouldFetchBatteryData(getState().system)) {
    return null;
  }

  return dispatch(fetchBatteryData(contractId, refreshBatteryData));
};

export const fetchNotifications = () => async dispatch => {
  try {
    const { data } = await GetGlobalNotificationMessages();
    dispatch(fetchNotificationsSuccess(data));
  } catch (e) {
    dispatch(fetchNotificationsFailure(e));
  }
};

export const shouldFetchNotifications = systemState => !systemState.get('notificationsLoaded');

export const fetchNotificationsIfNeeded = () => (dispatch, getState) => {
  if (!shouldFetchNotifications(getState().system)) {
    return null;
  }

  return dispatch(fetchNotifications());
};

// SELECTORS
const systemSelector = state => state.system;

export const getSystemOverview = createSelector([systemSelector], system => ({
  agreementNumber: system.get('agreementNumber'),
  timezone: system.get('timezone'),
  welcomeDate: system.get('welcomeDate'),
  status: system.get('status'),
  serviceContractName: system.get('serviceContractName'),
  systemSize: system.get('systemSize'),
  numberOfPanels: system.get('numPanels'),
  panelModel: system.get('panelModel'),
  panelMfg: system.get('panelMfg'),
  batteries: system.get('batteries'),
  inverters: system.get('inverters'),
  panels: system.get('panels'),
  alerts: system.get('systemAlerts'),
}));

export const getBatteryData = createSelector([systemSelector], system => {
  const batteryData = system.get('batteryData').get('data');
  return {
    data: batteryData,
    currentChargeAll: batteryData && batteryData.powerFlow && batteryData.powerFlow.STORAGE && batteryData.powerFlow.STORAGE.chargeLevel,
  };
});

export const getSystemLoading = createSelector([systemSelector], system => system.get('loading') || !system.get('loaded'));

export const getLastUpdatedLoading = createSelector([systemSelector], system => system.get('meterInfoLoading') || !system.get('meterInfoLoaded'));

export const getHasBattery = createSelector([systemSelector], system => {
  const batteryLength = system.get('batteries')?.length;
  return Boolean(batteryLength);
});

export const getPGThreshold = createSelector([systemSelector], system => {
  const pgThreshold = system.get('pgThreshold');
  if (pgThreshold === '0' || !pgThreshold) {
    return null;
  }
  return pgThreshold;
});

export const getBatteries = createSelector([systemSelector], system => system.get('batteries'));

export const getLastUpdatedReadings = createSelector([systemSelector], system => {
  const meterInfo = system.get('meterInfo');
  if (!meterInfo || !meterInfo.edges) {
    return null;
  }
  const readings = unwrapGraphQlList(meterInfo, 'edges');
  if (readings.length === 0) {
    return null;
  }
  const greatestDate = Math.max(
    ...readings.map(node => (node?.statusDetails?.readingEndTimeLocal ? new Date(node.statusDetails.readingEndTimeLocal).getTime() : null)),
  );
  // eslint-disable-next-line no-restricted-globals
  if (greatestDate && !isNaN(greatestDate) && greatestDate > 0) {
    return new Date(greatestDate).toISOString();
  }
  return null;
});

export const getHasNonCommAlert = createSelector([systemSelector], system => {
  const date = new Date();
  const systemAlerts = system.get('systemAlerts');
  const sevenDaysBefore = new Date(date.getTime() - 7 * 24 * 60 * 60 * 1000);
  return systemAlerts?.some(alert => {
    const withinPast7Days = sevenDaysBefore < new Date(alert.openDate);
    return alert.status === 'Non-Communication' && withinPast7Days;
  });
});

export const getNotifications = createSelector([systemSelector], system => system.get('notifications'));

// REDUCER
export const initialState = fromJS({
  loaded: false,
  loading: false,
  meterInfoLoading: false,
  meterInfoLoaded: false,
  error: null,
  agreementNumber: '',
  status: '',
  serviceContractName: '',
  systemSize: '',
  panelModel: '',
  panelMfg: '',
  numPanels: '',
  prospectId: '',
  batteries: [],
  inverters: [],
  panels: [],
  systemAlerts: [],
  allSites: [],
  pgThreshold: null,
  lastUpdatedReadings: [],
  meterInfo: null,
  batteryData: {
    data: {},
    loaded: false,
    loading: false,
  },
  notifications: [],
  notificationsLoaded: false,
});

export default (state = initialState, action) => {
  switch (action.type) {
    case FETCH_SYSTEM_OVERVIEW_SUCCESS:
      return state.set('loaded', true).set('loading', false).merge(action.payload);
    case FETCH_SYSTEM_OVERVIEW_FAILURE:
      return state.set('loading', false).set('loaded', true).set('error', action.payload);
    case FETCHING_SYSTEM_OVERVIEW:
      return state.set('loading', true);
    case FETCHING_BATTERY_DATA:
      return state.setIn(['batteryData', 'loading'], true);
    case FETCH_BATTERY_DATA_SUCCESS:
      return state.setIn(['batteryData', 'loaded'], true).setIn(['batteryData', 'loading'], false).setIn(['batteryData', 'data'], action.payload);
    case FETCH_BATTERY_DATA_FAILURE:
      return state.setIn(['batteryData', 'loading'], false).setIn(['batteryData', 'loaded'], true).set('error', action.payload);
    case UPDATING_SYSTEM_OVERVIEW:
      return state.set('updating', true);
    case UPDATE_SYSTEM_OVERVIEW:
      return state.set('updating', false).merge(action.payload);
    case FETCHING_METER_INFO:
      return state.set('meterInfoLoading', true);
    case FETCH_METER_INFO_FAILURE:
      return state.set('meterInfoLoading', false).set('Loaded', true).set('error', action.payload);
    case FETCH_METER_INFO_SUCCESS:
      return state.set('meterInfoLoaded', true).set('meterInfoLoading', false).set('meterInfo', action.payload);
    case FETCH_NOTIFICATIONS_SUCCESS:
      return state.set('notificationsLoaded', true).set('notifications', action.payload);
    case FETCH_NOTIFICATIONS_FAILURE:
      return state.set('notificationsLoaded', true);
    default:
      return state;
  }
};
